import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, signal } from "@angular/core";

import { ActivatedRoute, Router } from "@angular/router";

import { toSignal } from "@angular/core/rxjs-interop";

import { map } from "rxjs/operators";

import { FareRules } from "@models/fareRules";
import { CommonService } from "@services/common.service";
import { FlightSearchService } from "@services/flight-search.service";
import { YTBSEOService } from "@services/ytb-seo.service";

@Component({
  selector: "app-fare-rules",
  templateUrl: "./fare-rules.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FareRulesComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly flightsrv = inject(FlightSearchService);
  private readonly commonService = inject(CommonService);
  private readonly route = inject(ActivatedRoute);
  private readonly ytb = inject(YTBSEOService);
  private readonly router = inject(Router);

  // --------------------------------------------------------------------------
  // Signals
  // --------------------------------------------------------------------------

  readonly refareRuleRes = signal<any[]>([]);

  readonly fareRuleDetails = signal<FareRules | undefined>(undefined);

  readonly reshowSpinner = signal(false);

  readonly showFare = signal(false);

  readonly queryParams = signal<any>({});

  readonly isLoading = signal(false);

  readonly noFaresFound = signal(false);

  readonly errorMessageText = signal("");

  readonly queryParamsInfo = signal<any>({});

  readonly farerules = signal<any>(null);

  // --------------------------------------------------------------------------
  // Route query params
  // --------------------------------------------------------------------------

  readonly queryParamsSignal = toSignal(this.route.queryParams, {
    initialValue: {},
  });

  // --------------------------------------------------------------------------
  // Fare rules response
  // --------------------------------------------------------------------------

  readonly fareRulesResponse = toSignal(this.flightsrv.fareRulesSuccess$, {
    initialValue: null,
  });

  // --------------------------------------------------------------------------
  // Query params service response
  // --------------------------------------------------------------------------

  readonly queryParamsInfoResponse = toSignal(this.commonService.QUERYPARAMSGETSUCESS$, {
    initialValue: null,
  });

  constructor() {
    // ------------------------------------------------------------------------
    // Handle fare rules response
    // ------------------------------------------------------------------------

    effect(
      () => {
        const fare = this.fareRulesResponse();

        if (!fare) {
          return;
        }

        console.log(JSON.stringify(fare.tariffInfo));

        if (fare.messageInfo?.successCode && fare.tariffInfo && fare.tariffInfo.length > 0) {
          this.noFaresFound.set(false);

          this.reshowSpinner.set(true);

          this.refareRuleRes.set(fare.tariffInfo);
        } else {
          this.reshowSpinner.set(true);

          this.noFaresFound.set(true);

          this.errorMessageText.set("No Fare Rules Found for this Trip");
        }
      },
      {
        allowSignalWrites: true,
      },
    );

    // ------------------------------------------------------------------------
    // Handle query parameters
    // ------------------------------------------------------------------------

    effect(
      () => {
        const params = this.queryParamsSignal();

        if (!params) {
          return;
        }

        this.queryParams.set(params);

        const url = this.router.url.split("?");

        setTimeout(() => {
          this.ytb.createCanonicalURL(this.router.url);

          this.ytb.setDynamicTitle(url[0], {
            Rules: "Rules",
          });

          this.ytb.updateMetaTags(
            "desc",
            {
              replaceKeywords: {
                Rules: "Rules",
              },
            },
            "Homepage",
          );
        }, 1000);

        // Preserve existing functionality
        if (params["supplier"] !== undefined && params["IsPostBk"] !== undefined) {
          this.flightsrv.getFareRules({
            tempBookingNo: params["requestId"],
            supplierCode: params["supplier"],
          });
        } else {
          this.commonService.getQueryParamsInfo(params["requestId"]);
        }
      },
      {
        allowSignalWrites: true,
      },
    );

    // ------------------------------------------------------------------------
    // Handle query params service response
    // ------------------------------------------------------------------------

    effect(
      () => {
        const res = this.queryParamsInfoResponse();

        if (!res?.result?.requestObj) {
          return;
        }

        const queryParamsInfo = JSON.parse(res.result.requestObj);

        this.queryParamsInfo.set(queryParamsInfo);

        const fareRules = JSON.parse(queryParamsInfo.selectedData);

        console.log(JSON.stringify(fareRules));

        this.farerules.set(fareRules);

        this.flightsrv.getFareRules(fareRules);
      },
      {
        allowSignalWrites: true,
      },
    );
  }

  // --------------------------------------------------------------------------
  // Destroy
  // --------------------------------------------------------------------------

  ngOnDestroy(): void {
    this.flightsrv.dispose();
  }
}
